<template>
  <section
    v-if="socialWallVisible"
    :aria-label="socialWallTitle"
    :class="{
      'interactive-social-wall--mobile-collapsed': !shouldShowWidgetDetails,
      'interactive-social-wall--modal-open':
        commentsModalContext || likesModalContext,
    }"
    class="interactive-social-wall"
  >
    <header class="interactive-social-wall__header" @click="toggleMobileDetails">
      <div aria-hidden="true" class="interactive-social-wall__title-icon">
        <i class="uil-coffee"/>
      </div>

      <div class="interactive-social-wall__heading">
        <h3>{{ socialWallTitle }}</h3>
      </div>

      <button :aria-expanded="isMobileDetailsOpen"
              :class="{
          'interactive-social-wall__mobile-toggle--active': isMobileDetailsOpen,
        }"
              aria-controls="interactive-social-wall-details"
              :aria-label="isMobileDetailsOpen
                ? $t('general.hide_details')
                : $t('general.show_details')"
              class="interactive-social-wall__mobile-toggle"
              type="button"
              @click.stop="toggleMobileDetails"
      >
        <i aria-hidden="true" class="uil-angle-down"/>
      </button>
    </header>

    <div v-show="shouldShowWidgetDetails" id="interactive-social-wall-details" class="interactive-social-wall__details">
      <SocialWallComposer ref="postComposer"
                          :placeholder="sharePrompt"
                          :submit-error="postCreationError"
                          :submitting="isCreatingPost"
                          @submit="createPost"
                          @clear-error="postCreationError = ''"
      />

      <SocialWallFeed :is-loaded="isPostFeedReady"
                      :load-error="postsLoadError"
                      :post-items="postCardItems"
                      :skeleton-count="postSkeletonCount"
                      @delete="deletePost($event.post, $event.index)"
                      @detail="goToPostDetail($event.post)"
                      @retry="loadPosts(true)"
                      @toggle-menu="togglePostMenu($event.post, $event.index)"
                      @close-menu="closePostMenu"
                      @show-likes="openLikesModal($event.post, $event.index)"
                      @toggle-like="togglePostLike($event.post, $event.index)"
                      @show-comments="openCommentsModal($event.post, $event.index)"
      />
      <router-link class="interactive-social-wall__footer" to="/wall">
        <span class="interactive-social-wall__footer-label">
          <i aria-hidden="true" class="uil-users-alt"/>
          <strong>{{ footerLabel }}</strong>
        </span>
        <i aria-hidden="true" class="uil-arrow-right interactive-social-wall__footer-arrow"/>
      </router-link>
    </div>

    <SocialWallCommentsModal :busy="isCommentActionInProgress"
                             :comment-count="commentsModalCommentCount"
                             :comment-error="commentCreationError"
                             :comment-submitting="isCreatingComment"
                             :comments="commentsModalComments"
                             :current-user="currentUser"
                             :delete-errors="commentDeletionErrorById"
                             :deleting-comment-id="commentIdBeingDeleted"
                             :load-error="commentsModalLoadError"
                             :loading="isCommentsModalLoading"
                             :post="commentsModalPost"
                             :reply-error="replyCreationError"
                             :reply-submitting="isCreatingReply"
                             :visible="Boolean(commentsModalContext)"
                             @close="closeCommentsModal"
                             @retry="reloadCommentsModal"
                             @submit-comment="createComment"
                             @clear-comment-error="commentCreationError = ''"
                             @submit-reply="createReply"
                             @clear-reply-error="replyCreationError = ''"
                             @delete-comment="deleteComment"
    />

    <SocialWallLikesModal :is-loading="isLikesModalLoading"
                          :liked-users="usersWhoLikedPost"
                          :load-error="likesModalLoadError"
                          :visible="Boolean(likesModalContext)"
                          @close="closeLikesModal"
                          @retry="loadUsersWhoLikedPost"
    />
  </section>
</template>

<script>
import {
  CREATE_ITEM as REST_CREATE_ITEM,
  DELETE_ITEM_BY_ID as REST_DELETE_ITEM,
  GET_ITEMS as REST_GET_ITEMS,
} from "@/core/services/store/REST.module";
import { BASE_URL as POST_LIST_URL } from "@/core/services/store/social_post.module";
import { confirmDialogDelete } from "@/utils/confirmDialog";
import SocialWallCommentsModal from "./components/SocialWallCommentsModal.vue";
import SocialWallComposer from "./components/SocialWallComposer.vue";
import SocialWallFeed from "./components/SocialWallFeed.vue";
import SocialWallLikesModal from "./components/SocialWallLikesModal.vue";
import {
  apiErrorMessage,
  extractCreatedPost,
  extractLikedUsers,
  extractPostComments,
  extractReplyResponseComment,
  extractReplyResponseTree,
  extractWallPosts,
  hasCommentCollection,
  postCreationErrorMessage,
} from "./utils/apiPayload";
import {
  addReplyToTree,
  commentId,
  commentReplies,
  countCommentTree,
  ensureCommentUsers,
  findCommentInTree,
  removeCommentFromTree,
  replaceCommentInTree,
} from "./utils/commentTree";
import {
  isAwaitingAdminApproval,
  postRoleLabel as resolvePostRoleLabel,
  relativeTime,
  userName,
  userPhoto,
} from "./utils/presentation";
import { isOwnedBy } from "./utils/ownership";
import {
  hasRecordValue,
  withRecordValue,
  withoutRecordValue,
} from "./utils/state";

const POST_LIKE_URL = "api/social-post/like/";
const POST_COMMENT_URL = "api/social-post/comment/";
const POST_LIST_PAGE_SIZE = 6;
const MOBILE_VISIBLE_POST_LIMIT = 5;
const DESKTOP_POST_SKELETON_COUNT = 6;
const MOBILE_VIEWPORT_QUERY = "(max-width: 768px)";

export default {
  name: "SocialWallWidget",
  components: {
    SocialWallCommentsModal,
    SocialWallComposer,
    SocialWallFeed,
    SocialWallLikesModal,
  },
  props: {
    socialWallVisible: {
      type: Boolean,
      default: false,
    },
    socialWallTitle: {
      type: String,
      default: "",
    },
    lastPosts: {
      type: Array,
      default: () => [],
    },
    lastPostsLoaded: {
      type: Boolean,
      default: false,
    },
    currentUser: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const isMobileViewport =
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;

    return {
      isMobileViewport,
      isMobileDetailsOpen: false,
      socialPosts: [],
      hasLoadedPosts: false,
      isLoadingPosts: false,
      postsLoadError: "",
      isCreatingPost: false,
      postCreationError: "",
      postLikedByKey: {},
      postLikeCountByKey: {},
      postLikeUpdatingByKey: {},
      postActionErrorByKey: {},
      openPostMenuKey: "",
      postKeyBeingDeleted: "",
      likesModalContext: null,
      usersWhoLikedPost: [],
      isLikesModalLoading: false,
      likesModalLoadError: "",
      likesRequestSequence: 0,
      postCommentsByKey: {},
      postCommentCountByKey: {},
      commentsLoadedByPostKey: {},
      commentsLoadingByPostKey: {},
      commentsLoadErrorByPostKey: {},
      commentsModalContext: null,
      isCreatingComment: false,
      commentCreationError: "",
      isCreatingReply: false,
      replyCreationError: "",
      commentIdBeingDeleted: "",
      commentDeletionErrorById: {},
    };
  },
  computed: {
    visibleSocialPosts() {
      let posts = [];
      if (this.hasLoadedPosts) {
        posts = this.socialPosts;
      } else if (this.postsLoadError) {
        posts = this.lastPosts;
      }

      const postLimit = this.isMobileViewport
          ? MOBILE_VISIBLE_POST_LIMIT
          : POST_LIST_PAGE_SIZE;
      return (posts || []).filter(Boolean).slice(0, postLimit);
    },
    postCardItems() {
      return this.visibleSocialPosts.map((post, index) => ({
        key: this.getPostKey(post, index),
        post,
        index,
        authorName: userName(post?.user),
        authorPhoto: userPhoto(post?.user),
        roleLabel: resolvePostRoleLabel(post),
        timeText: relativeTime(post),
        isPersisted: this.hasPostId(post),
        isPendingApproval: isAwaitingAdminApproval(post),
        isMenuOpen: this.isPostMenuOpen(post, index),
        isDeleting: this.isPostDeleting(post, index),
        canDelete: this.canDeletePost(post),
        isLiked: this.isPostLiked(post, index),
        isLikeUpdating: this.isPostLikeUpdating(post, index),
        likeCount: this.getPostLikeCount(post, index),
        commentCount: this.getPostCommentCount(post, index),
        actionError: this.getPostActionError(post, index),
      }));
    },
    postSkeletonCount() {
      return this.isMobileViewport
          ? MOBILE_VISIBLE_POST_LIMIT
          : DESKTOP_POST_SKELETON_COUNT;
    },
    shouldShowWidgetDetails() {
      return !this.isMobileViewport || this.isMobileDetailsOpen;
    },
    isPostFeedReady() {
      return (
          this.hasLoadedPosts ||
          (Boolean(this.postsLoadError) && this.lastPostsLoaded)
      );
    },
    isCompanyAdmin() {
      const roles = Array.isArray(this.currentUser?.roles)
          ? this.currentUser.roles
          : [];
      return roles.some((role) => {
        const name = typeof role === "string" ? role : role?.name;
        return name === "company_admin" || name === "admin";
      });
    },
    commentsModalPost() {
      return this.commentsModalContext?.post || {};
    },
    commentsModalPostKey() {
      return this.commentsModalContext?.key || "";
    },
    commentsModalComments() {
      if (!this.commentsModalContext) return [];

      return this.getCommentsForPost(
          this.commentsModalPost,
          this.commentsModalPostKey
      );
    },
    commentsModalCommentCount() {
      if (!this.commentsModalContext) return 0;
      return this.getPostCommentCount(
          this.commentsModalPost,
          this.commentsModalContext.index
      );
    },
    isCommentsModalLoading() {
      return Boolean(this.commentsLoadingByPostKey[this.commentsModalPostKey]);
    },
    commentsModalLoadError() {
      return this.commentsLoadErrorByPostKey[this.commentsModalPostKey] || "";
    },
    isCommentActionInProgress() {
      return (
          this.isCreatingComment ||
          this.isCreatingReply ||
          Boolean(this.commentIdBeingDeleted)
      );
    },
    sharePrompt() {
      return this.$t("general.share_a");
    },
    footerLabel() {
      return this.$t("general.go_to_my_social_wall");
    },
  },
  mounted() {
    this.startMobileViewportTracking();
    if (this.socialWallVisible) this.loadPosts();
    if (typeof document !== "undefined") {
      document.addEventListener("click", this.closePostMenu);
      document.addEventListener("keydown", this.handleDocumentKeydown);
    }
  },
  beforeDestroy() {
    this.stopMobileViewportTracking();
    this.likesRequestSequence += 1;
    if (typeof document !== "undefined") {
      document.removeEventListener("click", this.closePostMenu);
      document.removeEventListener("keydown", this.handleDocumentKeydown);
    }
  },
  watch: {
    socialWallVisible(isVisible) {
      if (!isVisible) {
        this.isMobileDetailsOpen = false;
        this.closePostMenu();
      }
      if (isVisible && !this.hasLoadedPosts && !this.isLoadingPosts) {
        this.loadPosts();
      }
    },
  },
  methods: {
    startMobileViewportTracking() {
      if (
          typeof window === "undefined" ||
          typeof window.matchMedia !== "function"
      ) {
        return;
      }

      const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
      this._mobileViewportMediaQuery = mediaQuery;
      this.handleMobileViewportChange(mediaQuery);

      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", this.handleMobileViewportChange);
      } else if (typeof mediaQuery.addListener === "function") {
        mediaQuery.addListener(this.handleMobileViewportChange);
      }
    },
    stopMobileViewportTracking() {
      const mediaQuery = this._mobileViewportMediaQuery;
      if (!mediaQuery) return;

      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener(
            "change",
            this.handleMobileViewportChange
        );
      } else if (typeof mediaQuery.removeListener === "function") {
        mediaQuery.removeListener(this.handleMobileViewportChange);
      }

      this._mobileViewportMediaQuery = null;
    },
    handleMobileViewportChange(event) {
      this.isMobileViewport = Boolean(event?.matches);
    },
    toggleMobileDetails() {
      if (!this.isMobileViewport) return;

      this.isMobileDetailsOpen = !this.isMobileDetailsOpen;
      if (!this.isMobileDetailsOpen) this.closePostMenu();
    },
    async createPost({ text: submittedText = "", files = [] } = {}) {
      if (this.isCreatingPost) return;

      const text = String(submittedText).trim();
      if (text.length < 3) {
        this.postCreationError = this.$t(
            "general.social_post_minimum_length"
        );
        this.$refs.postComposer?.focusPostInput();
        return;
      }

      const postFiles = Array.isArray(files) ? files : [];
      const formData = new FormData();
      formData.append("text", text);
      postFiles.forEach((file) => {
        formData.append("files[]", file);
      });
      const hasPostFiles = postFiles.length > 0;

      this.isCreatingPost = true;
      this.postCreationError = "";

      try {
        const result = await this.$store.dispatch(REST_CREATE_ITEM, {
          url: POST_LIST_URL,
          contents: formData,
          acceptPromise: true,
        });

        if (!result || result.status === false) {
          this.postCreationError = postCreationErrorMessage(
              result,
              hasPostFiles
          );
          return;
        }

        const createdPost = extractCreatedPost(result.data);
        if (createdPost?.id !== undefined && createdPost?.id !== null) {
          const immediatePost = {
            ...createdPost,
            user: createdPost.user || this.currentUser,
          };
          this.socialPosts = [
            immediatePost,
            ...this.socialPosts.filter(
                (post) => String(post?.id) !== String(immediatePost.id)
            ),
          ].slice(0, POST_LIST_PAGE_SIZE);
          this.syncPostInteractionState(immediatePost, 0);
          this.hasLoadedPosts = true;
          this.postsLoadError = "";
        }

        this.$refs.postComposer?.resetComposer();
        await this.loadPosts(true);

        this.$refs.postComposer?.focusPostInput();
      } catch (error) {
        this.postCreationError = postCreationErrorMessage(
            error,
            hasPostFiles
        );
      } finally {
        this.isCreatingPost = false;
      }
    },
    buildPostListFilters(postId = null, pageSize = POST_LIST_PAGE_SIZE) {
      const filters = {
        like: null,
        page: 1,
        per_page: pageSize,
        sort: "id",
        order: "desc",
        id: postId,
        user_id: null,
      };

      if (!this.isCompanyAdmin) filters.only_general = 1;
      return filters;
    },
    async requestPostList(postFilters) {
      const result = await this.$store.dispatch(REST_GET_ITEMS, {
        url: POST_LIST_URL,
        filters: postFilters,
        acceptPromise: true,
      });

      if (!result) {
        throw new Error(this.$t("general.social_wall_response_not_received"));
      }
      if (result.status === false) throw result;
      return extractWallPosts(result.data);
    },
    async loadPosts(forceReload = false) {
      if (this.isLoadingPosts) return;
      if (this.hasLoadedPosts && !forceReload) return;

      this.isLoadingPosts = true;
      this.postsLoadError = "";

      try {
        const posts = await this.requestPostList(this.buildPostListFilters());
        this.socialPosts = posts;
        posts.forEach((post, index) => {
          this.syncPostInteractionState(post, index);
        });
        this.hasLoadedPosts = true;
      } catch (error) {
        this.postsLoadError = apiErrorMessage(
            error,
            this.$t("general.social_wall_posts_load_failed")
        );
      } finally {
        this.isLoadingPosts = false;
      }
    },
    syncPostInteractionState(post, index) {
      const key = this.getPostKey(post, index);
      const likeCount =
          post?.likes_count ?? post?.likesCount ?? post?.likes?.length;
      const commentCount =
          post?.comments_count ??
          post?.commentsCount ??
          post?.comments?.length ??
          post?.comments?.data?.length;
      const isLiked =
          post?.user_liked_count ??
          post?.is_liked ??
          post?.isLiked ??
          post?.liked_by_me;

      if (likeCount !== undefined && likeCount !== null) {
        this.postLikeCountByKey = withRecordValue(
            this.postLikeCountByKey,
            key,
            Number(likeCount) || 0
        );
      }
      if (
          !hasCommentCollection(post) &&
          commentCount !== undefined &&
          commentCount !== null
      ) {
        this.postCommentCountByKey = withRecordValue(
            this.postCommentCountByKey,
            key,
            Number(commentCount) || 0
        );
      }
      if (isLiked !== undefined && isLiked !== null) {
        this.postLikedByKey = withRecordValue(
            this.postLikedByKey,
            key,
            Boolean(isLiked)
        );
      }
      if (hasCommentCollection(post)) {
        this.storeLoadedPostComments(
            key,
            extractPostComments(post),
            commentCount
        );
      }
    },
    mergePostSnapshot(postSnapshot, fallbackPost, fallbackIndex) {
      const postIndex = this.socialPosts.findIndex(
          (post) => String(post?.id) === String(postSnapshot?.id)
      );
      const currentPost =
          postIndex > -1 ? this.socialPosts[postIndex] : fallbackPost || {};
      const mergedPost = { ...currentPost, ...postSnapshot };
      const interactionIndex = postIndex > -1 ? postIndex : fallbackIndex;

      if (postIndex > -1) {
        this.socialPosts = this.socialPosts.map((post, index) =>
            index === postIndex ? mergedPost : post
        );
      }
      this.syncPostInteractionState(mergedPost, interactionIndex);

      const key = this.getPostKey(mergedPost, interactionIndex);
      if (this.commentsModalPostKey === key && this.commentsModalContext) {
        this.commentsModalContext = {
          ...this.commentsModalContext,
          post: mergedPost,
          index: interactionIndex,
        };
      }

      return mergedPost;
    },
    async loadPostSnapshot(post, index) {
      if (!this.hasPostId(post)) {
        throw new Error(this.$t("general.post_information_not_found"));
      }

      const posts = await this.requestPostList(
          this.buildPostListFilters(post.id)
      );
      const postSnapshot =
          posts.find(
              (candidatePost) => String(candidatePost?.id) === String(post.id)
          ) || posts[0];

      if (!postSnapshot) {
        throw new Error(this.$t("general.post_not_found_in_list"));
      }
      return this.mergePostSnapshot(postSnapshot, post, index);
    },
    async tryRefreshPostSnapshot(post, index) {
      try {
        return await this.loadPostSnapshot(post, index);
      } catch {
        return null;
      }
    },
    getPostKey(post, index) {
      return `interactive-social-wall-post-${post?.id ?? index}`;
    },
    hasPostId(post) {
      return post?.id !== undefined && post?.id !== null;
    },
    canDeletePost(post) {
      return this.hasPostId(post) && isOwnedBy(post, this.currentUser);
    },
    isPostMenuOpen(post, index) {
      return this.openPostMenuKey === this.getPostKey(post, index);
    },
    togglePostMenu(post, index) {
      if (!this.hasPostId(post) || this.isPostDeleting(post, index)) return;
      const key = this.getPostKey(post, index);
      this.openPostMenuKey = this.openPostMenuKey === key ? "" : key;
    },
    closePostMenu() {
      this.openPostMenuKey = "";
    },
    handleDocumentKeydown(event) {
      if (event?.key !== "Escape") return;
      if (this.likesModalContext) {
        this.closeLikesModal();
        return;
      }
      this.closePostMenu();
    },
    isPostDeleting(post, index) {
      return this.postKeyBeingDeleted === this.getPostKey(post, index);
    },
    goToPostDetail(post) {
      if (!this.hasPostId(post)) return;
      this.closePostMenu();
      this.$router.push({
        name: "user.wall",
        params: { id: String(post.id) },
      });
    },
    openLikesModal(post, index) {
      if (!this.hasPostId(post)) return;
      this.closePostMenu();
      this.likesModalContext = {
        post,
        key: this.getPostKey(post, index),
      };
      this.usersWhoLikedPost = extractLikedUsers(post?.likes);
      this.likesModalLoadError = "";
      return this.loadUsersWhoLikedPost();
    },
    async loadUsersWhoLikedPost() {
      if (!this.likesModalContext?.post) return;

      const likedPostId = this.likesModalContext.post.id;
      const requestId = ++this.likesRequestSequence;
      this.isLikesModalLoading = true;
      this.likesModalLoadError = "";

      try {
        const result = await this.$store.dispatch(REST_GET_ITEMS, {
          url: `${POST_LIKE_URL}${likedPostId}/show`,
          filters: {},
          acceptPromise: true,
        });

        if (!result || result.status === false) throw result;
        if (requestId !== this.likesRequestSequence) return;
        this.usersWhoLikedPost = extractLikedUsers(result.data);
      } catch (error) {
        if (requestId !== this.likesRequestSequence) return;
        if (!this.usersWhoLikedPost.length) {
          this.likesModalLoadError = apiErrorMessage(
              error,
              this.$t("general.liked_users_load_failed")
          );
        }
      } finally {
        if (requestId === this.likesRequestSequence) {
          this.isLikesModalLoading = false;
        }
      }
    },
    closeLikesModal() {
      this.likesRequestSequence += 1;
      this.likesModalContext = null;
      this.usersWhoLikedPost = [];
      this.isLikesModalLoading = false;
      this.likesModalLoadError = "";
    },
    removePostInteractionState(postKey) {
      [
        "postLikedByKey",
        "postLikeCountByKey",
        "postLikeUpdatingByKey",
        "postActionErrorByKey",
        "postCommentsByKey",
        "postCommentCountByKey",
        "commentsLoadedByPostKey",
        "commentsLoadingByPostKey",
        "commentsLoadErrorByPostKey",
      ].forEach((statePropertyName) => {
        this[statePropertyName] = withoutRecordValue(
            this[statePropertyName],
            postKey
        );
      });
    },
    async deletePost(post, index) {
      if (!this.canDeletePost(post) || this.isPostDeleting(post, index)) {
        return;
      }

      const key = this.getPostKey(post, index);
      this.closePostMenu();
      const confirmed = await confirmDialogDelete({
        context: this,
        message: this.$t("general.confirm_social_post_deletion"),
      });
      if (!confirmed) return;

      this.postKeyBeingDeleted = key;
      this.postActionErrorByKey = withRecordValue(
          this.postActionErrorByKey,
          key,
          ""
      );

      try {
        const result = await this.$store.dispatch(REST_DELETE_ITEM, {
          url: `/api/social-post/${post.id}`,
          id: post.id,
          acceptPromise: true,
        });

        if (result?.status === false) {
          this.postActionErrorByKey = withRecordValue(
              this.postActionErrorByKey,
              key,
              apiErrorMessage(
                  result,
                  this.$t("general.social_post_delete_failed")
              )
          );
          return;
        }

        this.socialPosts = this.socialPosts.filter(
            (wallPost) => String(wallPost?.id) !== String(post.id)
        );
        if (this.commentsModalPostKey === key) this.closeCommentsModal();
        if (this.likesModalContext?.key === key) this.closeLikesModal();
        this.removePostInteractionState(key);
        await this.loadPosts(true);
      } catch (error) {
        this.postActionErrorByKey = withRecordValue(
            this.postActionErrorByKey,
            key,
            apiErrorMessage(
                error,
                this.$t("general.social_post_delete_failed")
            )
        );
      } finally {
        this.postKeyBeingDeleted = "";
      }
    },
    isPostLiked(post, index) {
      const key = this.getPostKey(post, index);
      if (hasRecordValue(this.postLikedByKey, key)) {
        return this.postLikedByKey[key];
      }

      return Boolean(
          post?.user_liked_count ??
          post?.is_liked ??
          post?.isLiked ??
          post?.liked_by_me ??
          false
      );
    },
    isPostLikeUpdating(post, index) {
      return Boolean(this.postLikeUpdatingByKey[this.getPostKey(post, index)]);
    },
    getPostActionError(post, index) {
      return this.postActionErrorByKey[this.getPostKey(post, index)] || "";
    },
    async togglePostLike(post, index) {
      if (!this.hasPostId(post) || this.isPostLikeUpdating(post, index)) return;

      const key = this.getPostKey(post, index);
      const wasLiked = this.isPostLiked(post, index);
      const previousCount = this.getPostLikeCount(post, index);

      this.postLikeUpdatingByKey = withRecordValue(
          this.postLikeUpdatingByKey,
          key,
          true
      );
      this.postActionErrorByKey = withRecordValue(
          this.postActionErrorByKey,
          key,
          ""
      );

      try {
        const result = await this.$store.dispatch(REST_CREATE_ITEM, {
          url: POST_LIKE_URL + post.id,
          acceptPromise: true,
        });

        if (!result || result.status === false) {
          this.postActionErrorByKey = withRecordValue(
              this.postActionErrorByKey,
              key,
              apiErrorMessage(
                  result,
                  this.$t("general.social_post_like_action_failed")
              )
          );
          return;
        }

        const nextLiked = !wasLiked;
        const responseLikes = Array.isArray(result.data) ? result.data : null;
        const nextCount = responseLikes
            ? responseLikes.length
            : Math.max(0, previousCount + (nextLiked ? 1 : -1));

        this.postLikedByKey = withRecordValue(
            this.postLikedByKey,
            key,
            nextLiked
        );
        this.postLikeCountByKey = withRecordValue(
            this.postLikeCountByKey,
            key,
            nextCount
        );
        await this.tryRefreshPostSnapshot(post, index);
      } catch (error) {
        this.postActionErrorByKey = withRecordValue(
            this.postActionErrorByKey,
            key,
            apiErrorMessage(
                error,
                this.$t("general.social_post_like_action_failed")
            )
        );
      } finally {
        this.postLikeUpdatingByKey = withRecordValue(
            this.postLikeUpdatingByKey,
            key,
            false
        );
      }
    },
    getPostLikeCount(post, index) {
      const key = this.getPostKey(post, index);
      if (hasRecordValue(this.postLikeCountByKey, key)) {
        return Number(this.postLikeCountByKey[key]) || 0;
      }

      return Number(
          post?.likes_count ?? post?.likesCount ?? post?.likes?.length ?? 0
      );
    },
    getPostCommentCount(post, index) {
      const key = this.getPostKey(post, index);
      const apiReportedCount = hasRecordValue(this.postCommentCountByKey, key)
          ? Number(this.postCommentCountByKey[key]) || 0
          : Number(post?.comments_count ?? post?.commentsCount ?? 0) || 0;
      const comments = this.getCommentsForPost(post, key);

      return Math.max(apiReportedCount, countCommentTree(comments));
    },
    getCommentsForPost(post, postKey) {
      if (hasRecordValue(this.postCommentsByKey, postKey)) {
        return Array.isArray(this.postCommentsByKey[postKey])
            ? this.postCommentsByKey[postKey]
            : [];
      }

      return extractPostComments(post);
    },
    storeLoadedPostComments(postKey, comments, minimumCommentCount = 0) {
      const normalizedComments = Array.isArray(comments) ? comments : [];
      const commentCount = Math.max(
          Number(minimumCommentCount) || 0,
          countCommentTree(normalizedComments)
      );

      this.postCommentsByKey = withRecordValue(
          this.postCommentsByKey,
          postKey,
          normalizedComments
      );
      this.postCommentCountByKey = withRecordValue(
          this.postCommentCountByKey,
          postKey,
          commentCount
      );
      this.commentsLoadedByPostKey = withRecordValue(
          this.commentsLoadedByPostKey,
          postKey,
          true
      );
      this.commentsLoadErrorByPostKey = withRecordValue(
          this.commentsLoadErrorByPostKey,
          postKey,
          ""
      );
    },
    canDeleteComment(comment) {
      return Boolean(
          commentId(comment) && isOwnedBy(comment, this.currentUser)
      );
    },
    async deleteComment(comment) {
      const selectedCommentId = commentId(comment);
      if (
          !this.commentsModalContext ||
          !this.canDeleteComment(comment) ||
          this.isCreatingComment ||
          this.isCreatingReply ||
          this.commentIdBeingDeleted
      ) {
        return;
      }

      const activePostContext = { ...this.commentsModalContext };
      this.commentIdBeingDeleted = selectedCommentId;
      this.commentDeletionErrorById = withRecordValue(
          this.commentDeletionErrorById,
          selectedCommentId,
          ""
      );

      try {
        const result = await this.$store.dispatch(REST_DELETE_ITEM, {
          url: `/api/comment/${selectedCommentId}`,
          id: selectedCommentId,
          acceptPromise: true,
        });

        if (result?.status === false) {
          this.commentDeletionErrorById = withRecordValue(
              this.commentDeletionErrorById,
              selectedCommentId,
              apiErrorMessage(
                  result,
                  this.$t("general.social_post_comment_delete_failed")
              )
          );
          return;
        }

        let currentComments = this.getCommentsForPost(
            activePostContext.post,
            activePostContext.key
        );
        const previousCount = this.getPostCommentCount(
            activePostContext.post,
            activePostContext.index
        );
        const removalResult = removeCommentFromTree(
            currentComments,
            selectedCommentId
        );
        currentComments = removalResult.comments;
        const removedComment = removalResult.removedComment;
        const removedCount = removedComment
            ? countCommentTree([removedComment])
            : 1;
        const nextCount = Math.max(0, previousCount - removedCount);

        this.storeLoadedPostComments(
            activePostContext.key,
            currentComments,
            nextCount
        );

        const optimisticComments = currentComments;
        const refreshedPost = await this.tryRefreshPostSnapshot(
            activePostContext.post,
            activePostContext.index
        );

        if (refreshedPost && hasCommentCollection(refreshedPost)) {
          const refreshedComments = ensureCommentUsers(
              extractPostComments(refreshedPost),
              this.currentUser
          );
          const finalComments = findCommentInTree(
              refreshedComments,
              selectedCommentId
          )
              ? optimisticComments
              : refreshedComments;
          this.storeLoadedPostComments(
              activePostContext.key,
              finalComments,
              nextCount
          );
        }

        this.commentDeletionErrorById = withRecordValue(
            this.commentDeletionErrorById,
            selectedCommentId,
            ""
        );
      } catch (error) {
        this.commentDeletionErrorById = withRecordValue(
            this.commentDeletionErrorById,
            selectedCommentId,
            apiErrorMessage(
                error,
                this.$t("general.social_post_comment_delete_failed")
            )
        );
      } finally {
        this.commentIdBeingDeleted = "";
      }
    },
    async createReply({ comment, text: submittedText = "" } = {}) {
      const text = String(submittedText).trim();
      const targetCommentId = commentId(comment);
      if (
          !text ||
          !targetCommentId ||
          !this.commentsModalContext ||
          !this.hasPostId(this.commentsModalContext.post) ||
          this.isCommentActionInProgress
      ) {
        return;
      }

      const activePostContext = { ...this.commentsModalContext };
      const formData = new FormData();
      formData.append("text", text);
      formData.append("parent_id", targetCommentId);
      this.isCreatingReply = true;
      this.replyCreationError = "";

      try {
        const result = await this.$store.dispatch(REST_CREATE_ITEM, {
          url: `${POST_COMMENT_URL}${activePostContext.post.id}/reply/${targetCommentId}`,
          contents: formData,
          acceptPromise: true,
        });

        if (!result || result.status === false) {
          this.replyCreationError = apiErrorMessage(
              result,
              this.$t("general.social_post_reply_send_failed")
          );
          return;
        }

        let currentComments = this.getCommentsForPost(
            activePostContext.post,
            activePostContext.key
        );
        const previousCount = this.getPostCommentCount(
            activePostContext.post,
            activePostContext.index
        );
        const responseTree = ensureCommentUsers(
            extractReplyResponseTree(result.data),
            this.currentUser
        );
        const updatedTarget = findCommentInTree(responseTree, targetCommentId);
        let replyApplied = false;

        if (updatedTarget) {
          const replacementResult = replaceCommentInTree(
              currentComments,
              targetCommentId,
              updatedTarget
          );
          currentComments = replacementResult.comments;
          replyApplied = replacementResult.changed;
        }

        if (!replyApplied) {
          const normalizedReply = this.normalizeCreatedComment(
              extractReplyResponseComment(result.data),
              text
          );
          const reply = {
            ...normalizedReply,
            parent_id: normalizedReply.parent_id ?? targetCommentId,
            approved_replies: commentReplies(normalizedReply),
          };
          const additionResult = addReplyToTree(
              currentComments,
              targetCommentId,
              reply
          );
          currentComments = additionResult.comments;
          replyApplied = additionResult.changed;
        }

        if (!replyApplied) {
          throw new Error(this.$t("general.social_post_reply_add_failed"));
        }

        this.storeLoadedPostComments(
            activePostContext.key,
            currentComments,
            previousCount + 1
        );

        const optimisticComments = currentComments;
        const optimisticCount = countCommentTree(optimisticComments);
        const refreshedPost = await this.tryRefreshPostSnapshot(
            activePostContext.post,
            activePostContext.index
        );

        if (refreshedPost && hasCommentCollection(refreshedPost)) {
          const refreshedComments = ensureCommentUsers(
              extractPostComments(refreshedPost),
              this.currentUser
          );
          const finalComments =
              countCommentTree(refreshedComments) >= optimisticCount
                  ? refreshedComments
                  : optimisticComments;
          this.storeLoadedPostComments(
              activePostContext.key,
              finalComments,
              previousCount + 1
          );
        }
      } catch (error) {
        this.replyCreationError = apiErrorMessage(
            error,
            this.$t("general.social_post_reply_send_failed")
        );
      } finally {
        this.isCreatingReply = false;
      }
    },
    async loadPostComments(post, index, forceReload = false) {
      const key = this.getPostKey(post, index);
      if (this.commentsLoadingByPostKey[key]) return;
      if (this.commentsLoadedByPostKey[key] && !forceReload) return;

      if (!this.hasPostId(post)) {
        this.commentsLoadErrorByPostKey = withRecordValue(
            this.commentsLoadErrorByPostKey,
            key,
            this.$t("general.comments_load_failed_missing_post_information")
        );
        return;
      }

      this.commentsLoadingByPostKey = withRecordValue(
          this.commentsLoadingByPostKey,
          key,
          true
      );
      this.commentsLoadErrorByPostKey = withRecordValue(
          this.commentsLoadErrorByPostKey,
          key,
          ""
      );

      try {
        const listedPost = await this.loadPostSnapshot(post, index);
        const comments = extractPostComments(listedPost);

        this.storeLoadedPostComments(
            key,
            comments,
            this.getPostCommentCount(listedPost, index)
        );
      } catch (error) {
        this.commentsLoadedByPostKey = withRecordValue(
            this.commentsLoadedByPostKey,
            key,
            false
        );
        this.commentsLoadErrorByPostKey = withRecordValue(
            this.commentsLoadErrorByPostKey,
            key,
            apiErrorMessage(
                error,
                this.$t("general.social_post_comments_load_failed")
            )
        );
      } finally {
        this.commentsLoadingByPostKey = withRecordValue(
            this.commentsLoadingByPostKey,
            key,
            false
        );
      }
    },
    async openCommentsModal(post, index) {
      this.commentsModalContext = {
        post,
        index,
        key: this.getPostKey(post, index),
      };
      this.commentCreationError = "";
      this.replyCreationError = "";
      this.commentDeletionErrorById = {};

      await this.loadPostComments(post, index);
    },
    closeCommentsModal() {
      if (this.isCommentActionInProgress) return;
      this.commentsModalContext = null;
      this.commentCreationError = "";
      this.replyCreationError = "";
      this.commentDeletionErrorById = {};
    },
    reloadCommentsModal() {
      if (!this.commentsModalContext) return;
      return this.loadPostComments(
          this.commentsModalContext.post,
          this.commentsModalContext.index,
          true
      );
    },
    async createComment(submittedText = "") {
      const text = String(submittedText).trim();
      if (
          !text ||
          !this.commentsModalContext ||
          this.isCommentActionInProgress ||
          this.isCommentsModalLoading
      ) {
        return;
      }

      const activePostContext = { ...this.commentsModalContext };
      if (!this.hasPostId(activePostContext.post)) return;

      const formData = new FormData();
      formData.append("text", text);
      this.isCreatingComment = true;
      this.commentCreationError = "";

      try {
        const result = await this.$store.dispatch(REST_CREATE_ITEM, {
          url: POST_COMMENT_URL + activePostContext.post.id,
          contents: formData,
          acceptPromise: true,
        });

        if (!result || result.status === false) {
          this.commentCreationError = apiErrorMessage(
              result,
              this.$t("general.social_post_comment_send_failed")
          );
          return;
        }

        const newComment = this.normalizeCreatedComment(result.data, text);
        const existingComments = this.getCommentsForPost(
            activePostContext.post,
            activePostContext.key
        );
        const nextCommentCount =
            this.getPostCommentCount(
                activePostContext.post,
                activePostContext.index
            ) + 1;

        this.storeLoadedPostComments(
            activePostContext.key,
            [newComment, ...existingComments],
            nextCommentCount
        );

        const refreshedPost = await this.tryRefreshPostSnapshot(
            activePostContext.post,
            activePostContext.index
        );
        if (refreshedPost && hasCommentCollection(refreshedPost)) {
          const refreshedComments = extractPostComments(refreshedPost);
          const newCommentIsListed =
              newComment?.id !== undefined &&
              newComment?.id !== null &&
              refreshedComments.some(
                  (comment) => String(comment?.id) === String(newComment.id)
              );

          this.storeLoadedPostComments(
              activePostContext.key,
              newCommentIsListed
                  ? refreshedComments
                  : [newComment, ...refreshedComments],
              nextCommentCount
          );
        }
      } catch (error) {
        this.commentCreationError = apiErrorMessage(
            error,
            this.$t("general.social_post_comment_send_failed")
        );
      } finally {
        this.isCreatingComment = false;
      }
    },
    normalizeCreatedComment(comment, fallbackText) {
      const normalizedComment =
          comment && typeof comment === "object" && !Array.isArray(comment)
              ? { ...comment }
              : {};

      return {
        ...normalizedComment,
        user_id:
            normalizedComment.user_id ??
            normalizedComment.user?.id ??
            this.currentUser?.id,
        text: normalizedComment.text || fallbackText,
        created_at: normalizedComment.created_at || new Date().toISOString(),
        user: normalizedComment.user || {
          id: this.currentUser?.id,
          name: this.currentUser?.name,
          surname: this.currentUser?.surname,
          fullname: userName(this.currentUser, this.$t("general.you")),
          photo_link: userPhoto(this.currentUser),
        },
      };
    },
  },
};
</script>

<style scoped src="./SocialWallWidget.css"></style>
